import React, { Dispatch, Key, useMemo, useState } from 'react';
import { Badge, Button, Table, TableColumnType } from 'antd';
import { FilterDropdownProps, TablePaginationConfig } from 'antd/lib/table/interface';
import { SearchOutlined } from '@ant-design/icons';

import { PupDocsRoutes } from 'pupdocs/utilities/const';
import { PreFlight } from 'pupdocs/types';
import { TableFilterDropdownStyled } from 'shared/components/table/TableFilterDropdown.styles';
import colors from 'theme/colors';
import { dateSort, defaultSort } from 'utilities/sorts/sorts';
import { filterTableValue } from 'utilities/table/table';
import { FacetList, TableFilter } from 'types';
import { PreFlightsAction, PreFlightsState } from '../preFlights.state';

function getColumns(
  filters: TableFilter,
  facets: FacetList,
  dispatch: Dispatch<PreFlightsAction>,
) {
  return useMemo<TableColumnType<PreFlight>[]>(
    () => [
      {
        title: 'Puppy ID',
        dataIndex: ['puppy', 'id'],
        filteredValue: filters?.['puppy.id'] ?? null,
        onFilter: (value, record: PreFlight) =>
          filterTableValue(value, String(record.puppy.id)),
        filterIcon: (filtered) => (
          <SearchOutlined style={{ color: filtered ? colors.primary : undefined }} />
        ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          prefixCls,
          visible,
        }: FilterDropdownProps) => (
          <TableFilterDropdownStyled
            dataIndex="puppy.id"
            onSearch={(value: React.Key[], dataIndex: string) => {
              dispatch({
                type: 'FILTER_PRE_FLIGHTS',
                payload: { ...filters, [dataIndex]: value },
              });
            }}
            prefixCls={prefixCls}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            visible={visible}
          />
        ),
      },
      {
        title: 'Customer ID',
        dataIndex: ['customer', 'id'],
        filteredValue: filters?.['customer.id'] ?? null,
        onFilter: (value, record: PreFlight) =>
          filterTableValue(value, String(record.customer.id)),
        filterIcon: (filtered) => (
          <SearchOutlined style={{ color: filtered ? colors.primary : undefined }} />
        ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          prefixCls,
          visible,
        }: FilterDropdownProps) => (
          <TableFilterDropdownStyled
            dataIndex="customer.id"
            onSearch={(value: React.Key[], dataIndex: string) => {
              dispatch({
                type: 'FILTER_PRE_FLIGHTS',
                payload: { ...filters, [dataIndex]: value },
              });
            }}
            prefixCls={prefixCls}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            visible={visible}
          />
        ),
      },
      {
        title: 'Breeder ID',
        dataIndex: ['breeder', 'id'],
        filteredValue: filters?.['breeder.id'] ?? null,
        onFilter: (value, record: PreFlight) =>
          filterTableValue(value, String(record.breeder.id)),
        filterIcon: (filtered) => (
          <SearchOutlined style={{ color: filtered ? colors.primary : undefined }} />
        ),
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          prefixCls,
          visible,
        }: FilterDropdownProps) => (
          <TableFilterDropdownStyled
            dataIndex="breeder.id"
            onSearch={(value: React.Key[], dataIndex: string) => {
              dispatch({
                type: 'FILTER_PRE_FLIGHTS',
                payload: { ...filters, [dataIndex]: value },
              });
            }}
            prefixCls={prefixCls}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            visible={visible}
          />
        ),
      },
      {
        title: 'Breeder',
        dataIndex: ['breeder', 'primaryUser', 'firstName'],
        sorter: (a, b) =>
          defaultSort(
            a.breeder.primaryUser.firstName ?? '',
            b.breeder.primaryUser.firstName ?? '',
          ),
        sortDirections: ['descend', 'ascend'],
        filters: facets?.breeder,
        filteredValue: filters?.['breeder.primaryUser.firstName'] ?? null,
        onFilter: (value, record: PreFlight) =>
          filterTableValue(value, String(record.breeder.primaryUser.firstName)),
      },
      {
        title: 'Progress',
        dataIndex: ['progress', 'progressString'],
        sorter: (a, b) =>
          defaultSort(
            a.breeder.primaryUser.firstName ?? '',
            b.breeder.primaryUser.firstName ?? '',
          ),
        sortDirections: ['descend', 'ascend'],
        filters: facets?.progress,
        filteredValue: filters?.['progress.progressString'] ?? null,
        onFilter: (value, record: PreFlight) =>
          filterTableValue(value, String(record ? record?.progress?.progressString : '')),
        render: (text: string, record: PreFlight) => {
          const render = record.documents.map((doc, index) => {
            const status =
              doc.workflowEntity.currentStatusId === 25 ? 'success' : 'default';
            return <Badge status={status} key={`${record.id}${index}`} />;
          });
          return (
            <>
              {render}
              {text}
            </>
          );
        },
      },
      {
        title: 'Review Status',
        dataIndex: 'status',
      },
      {
        title: 'Expiration date',
        dataIndex: 'expiresOn',
        sorter: (a, b) => dateSort(a?.expiresOn as string, b?.expiresOn ?? ''),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Travel date',
        dataIndex: 'travelDate',
        sorter: (a, b) =>
          dateSort(a?.travelDate as string, (b?.travelDate as string) ?? ''),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Last edit',
        dataIndex: ['lastDocumentEvent', 'occurredAt'],
        sorter: (a, b) =>
          dateSort(
            a?.lastDocumentEvent?.occurredAt as string,
            (b?.lastDocumentEvent?.occurredAt as string) ?? '',
          ),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Last reviewed',
        dataIndex: ['lastDocumentEvent'],
      },
      {
        title: 'Action',
        dataIndex: ['id'],
        align: 'center' as const,
        render: (lineItemId: string) => (
          <a href={`${PupDocsRoutes.FileDetails}/${lineItemId}`}>
            <Button type="primary">Review</Button>
          </a>
        ),
      },
    ],
    [filters, facets],
  );
}

interface Props {
  preFlightsState: PreFlightsState;
  dispatch: Dispatch<PreFlightsAction>;
}

export default function PreFlightsTable(props: Props): React.ReactElement {
  const [pageSize, setPageSize] = useState(25);
  const {
    preFlightsState: { loading, filters, preFlights, facets },
    dispatch,
  } = props;

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
  ): void => {
    if (paginationConfig.pageSize) setPageSize(paginationConfig.pageSize);
    dispatch({ type: 'FILTER_PRE_FLIGHTS', payload: filters as TableFilter });
  };

  const columns = getColumns(filters, facets, dispatch);

  return (
    <Table<PreFlight>
      onChange={handleTableChange}
      columns={columns}
      loading={loading}
      dataSource={preFlights}
      pagination={{ position: ['bottomCenter'], pageSize, showSizeChanger: true }}
    />
  );
}
